import { useState, useEffect } from 'react';
import { debounce } from 'lodash';
import { Divider, Grid2 as Grid, Typography, Alert, Stack, FormControl, OutlinedInput, InputAdornment, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import { Search } from '@mui/icons-material';

import { useErrorBoundary } from 'react-error-boundary';
import YtdReport from '../../../types/reports/ytdReport';
import YearToDateReportsTable from './YearToDateReportsTable';
import ReportListExportSession from './ReportListExportSection';
import PageBreadCrumbs from '../../../components/PageBreadcrumbs';

const ActiveBallotQuestionReports = (): JSX.Element => {
    
    document.title = 'OCPF - Active Ballot Question Committee Reports';

    const { showBoundary } = useErrorBoundary(); 
    // go back to last 10 years? 
    const dropDownYears = Array.from(new Array(10), (val, index) => new Date().getFullYear() - index);
    const [isLoading, setIsLoading] = useState(true);
    const [tableRows, setRows] = useState<YtdReport[]>();
    const [reports, setReports] = useState<YtdReport[]>();
    const [reportsSummary, setReportsSummary] = useState('No reports found.');
    const [reportYear, setReportYear] = useState(() => {
      if (new Date().getFullYear() % 2 === 0) {
        return new Date().getMonth() <= 7 ? new Date().getFullYear() - 1 : new Date().getFullYear();
      }

      return new Date().getFullYear() - 1;
    });

    const [excelExportUrl, setExcelExportUrl] = useState('');
    const [txtExportUrl, setTxtExportUrl] = useState('');


  useEffect(() => {
    getBallotQuestionReportList();
  }, [reportYear])

  const getBallotQuestionReportList = async () => {

    setExcelExportUrl(`${process.env.REACT_APP_API_BASE_URL}/reports/ballotQuestions/excelOutput/${reportYear}`);
    setTxtExportUrl(`${process.env.REACT_APP_API_BASE_URL}/reports/ballotQuestions/textOutput/${reportYear}`);

    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/ballotquestions/${reportYear}`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get active ballot question committees reports.'));
          }
          return result.json();
        })
        .then(result => {
          let count = result.length;
          if (count === 0) {
            setReportsSummary("No reports found.");
            setRows([]);
          }
          else {
            setReportsSummary(`Viewing 1 through ${count} of ${count} reports`);
            setRows(result);
            setReports(result);
          }
          setIsLoading(false);
        });
    } catch (error) {
      console.error('Uncaught Error:', error);
      showBoundary(new Error(`${error}`));
    }
  }

  const updateReporYear = (event: any) => {
    setReportYear(event.target.value);
  };

  const filterByName = debounce((event: any) => {
    let searchInput = event.target.value.toLowerCase();
    if (searchInput.length === 0) {
      setRows(reports);
    }
    else {
      let filteredRows = reports?.filter((row) => row.filerName.toLowerCase().includes(searchInput)) || [];
      let count = filteredRows.length;
      setReportsSummary(`Viewing 1 through ${count} of ${count} reports`);
      setRows(filteredRows);
    }
  }, 800);

  return (
    <Main>
      <Container>
        <PageBreadCrumbs></PageBreadCrumbs>
        <Typography
          variant="h5"
          align={'left'}
          data-aos={'fade-up'}
          gutterBottom
          sx={{
            fontWeight: 700,
          }}
          paddingTop={3}
          marginBottom={2}
        >
          Active Ballot Question Committee Reports
        </Typography>
        <Divider />
        <Grid container marginBottom={2} marginTop={3}>
          <Grid size={12}>
            <Typography marginBottom={3} component={'p'} variant='body1'>
              Ballot question committees file annual reports, called "year-end reports", to disclose activity for the calendar year.
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>
            <Stack direction={"row"} spacing={1}
              sx={{ mb: 1 }}
            >
              <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                <InputLabel id="year-id">Year</InputLabel>
                <Select
                  sx={{ fontSize: 'smaller' }}
                  label="Report Year"
                  labelId="year-id"
                  id="year-select"
                  aria-labelledby="year-id"
                  defaultValue={reportYear}
                  onChange={updateReporYear}
                >
                  {
                    dropDownYears.map((year) =>
                      <MenuItem value={year} key={year} >{year}</MenuItem>
                    )
                  }
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ m: 1, p: 0, maxWidth: 300 }}>
                <OutlinedInput
                  placeholder={"Filter by committee name"}
                  startAdornment={
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  }
                  aria-label="Filter by committee name"
                  onChange={filterByName}
                  sx={{ fontSize: 'smaller' }}
                />

              </FormControl>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <ReportListExportSession excelExportUrl={excelExportUrl} txtExportUrl={txtExportUrl}></ReportListExportSession>
          </Grid>
          <Grid size={11.5}>
            <Alert icon={false} severity="info"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                '.MuiAlert-message': {
                  padding: '4px',
                }
              }}
            >
              {reportsSummary}
            </Alert>
          </Grid>
        </Grid>
        {/*report table*/}
        {/*report table*/}
        <YearToDateReportsTable tableRows={tableRows} isCandidate={false}></YearToDateReportsTable>
        {/*loading indicator*/}
        <CircularProgress
          size={50}
          className={isLoading ? "" : "hidden"}
          sx={{
            position: "fixed",
            left: "50%",
            top: "20%",
            transform: "translate(-50%, -50%)",
            zIndex: 2
          }}
        />
      </Container>
    </Main>
  );
};

export default ActiveBallotQuestionReports;
